import { DNABox, DNAButton, DNAText, luxColors, useWindowSize, useVisualViewportSize, debounce } from '@alucio/lux-ui';
import mergeStyles from '@alucio/lux-ui/lib/components/util/mergeStyles';
import DNAIcon from '@alucio/lux-ui/src/components/visual/DNAIcon/DNAIcon';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Platform, ScrollView, StyleSheet } from 'react-native';
import { useVideoContext } from '@alucio/video';
import InputComponent from '../InputComponent/InputComponent';
import Iffy from '@alucio/lux-ui/src/components/util/Iffy/Iffy';
import { BeaconChatData } from '../../types/DatatrackTypes';
import { useDatatrackContext } from '../AlucioVideoProvider/VideoProvider/DatatrackProvider';
import { isMobile } from 'react-device-detect';
export const Chat = () => {
    const windowSize = useWindowSize();
    const vvpSize = useVisualViewportSize();
    const [unsentMsg, setUnsentMsg] = useState();
    const styles = StyleSheet.create({
        container: {
            backgroundColor: '#FFFFFF',
            zIndex: 1,
            maxWidth: 280,
        },
        mobileContainer: {
            backgroundColor: '#FFFFFF',
            width: windowSize.width,
            height: (vvpSize === null || vvpSize === void 0 ? void 0 : vvpSize.height) || windowSize.height,
            position: 'absolute',
            overflow: 'hidden',
            bottom: 0,
            left: 0,
            top: 0,
            zIndex: 1,
        },
        header: {
            backgroundColor: luxColors.virtual.border,
            height: 40,
            padding: 13,
        },
        mobileHeader: {
            height: 48,
            borderBottomWidth: 1,
            borderBottomColor: '#DBDBDB',
            borderStyle: 'solid',
        },
        headerElement: {
            color: 'rgba(255, 255, 255, 0.4)',
            fontSize: 14,
        },
        headerTitle: {
            marginRight: 'auto',
        },
        mobileCloseHeader: {
            marginLeft: 16,
            marginRight: 16,
            position: 'absolute',
            fontWeight: 500,
            right: 0,
        },
        mobileHeaderTitle: {
            color: 'black',
            fontSize: 20,
            textAlign: 'center',
            flexGrow: 1,
        },
        body: {
            backgroundColor: 'white',
            padding: 8,
            height: vvpSize ? (vvpSize === null || vvpSize === void 0 ? void 0 : vvpSize.height) - 260 : 400,
        },
        mobileBody: {
            backgroundColor: 'white',
            padding: 8,
            // Accounts for header & text box height + padding
            height: (vvpSize === null || vvpSize === void 0 ? void 0 : vvpSize.height) - 145,
        },
        chatMsgBox: {
            marginBottom: 4,
        },
        chatUser: {
            // TODO: use correct color or add to styles
            color: luxColors.contentText.secondary,
        },
        chatUserHost: {
            // TODO: use correct color or add to styles
            color: luxColors.primary.primary,
        },
        chatMsg: {
            lineHeight: 20,
            fontSize: 13,
            borderRadius: 4,
            paddingHorizontal: 12,
            paddingVertical: 8,
            marginTop: 4,
            maxWidth: '90%',
        },
        remoteChatMsg: {
            backgroundColor: '#F1F1F1',
            marginRight: 'auto',
        },
        localChatMsg: {
            color: 'white',
            backgroundColor: '#44A2DA',
            marginLeft: 'auto',
        },
        inputWrapper: {
            margin: 8,
        },
        sendButtonStyle: {
            marginLeft: 4,
            width: 48,
            height: 48,
        },
    });
    const [messages, setMessages] = useState();
    const { room, chatLog, addChatMsg, chatVisible, toggleChatVis } = useVideoContext();
    const chatBody = useRef();
    const { emitDatatrackMsg } = useDatatrackContext();
    const [isScrolledToBottom, setIsScrolledToBottom] = useState(true);
    const debouncedScrollToEnd = useCallback(debounce(() => {
        var _a;
        (_a = chatBody.current) === null || _a === void 0 ? void 0 : _a.scrollToEnd({ animated: false });
    }, 300), []);
    /**
     * This handler fires when the input has a change in text and captures the updated value as a stateful value
     * @param msg string
     */
    const onMsgEdit = (msg) => {
        setUnsentMsg(msg);
    };
    /**
     * This handler fires on input field submit and generates a BeaconChatData object after identifying
     * the user identity and whether they are a host. Once the BeaconChatData msg has been generated it is
     * both added to the chatLog as well as passed along to the dataTrack for consumption by the attendees
     */
    const onMsgSend = () => {
        var _a, _b;
        if (unsentMsg) {
            const user = (_b = (_a = room.localParticipant) === null || _a === void 0 ? void 0 : _a.identity) === null || _b === void 0 ? void 0 : _b.split('.').slice(2).join('.');
            const sid = room.localParticipant.sid;
            const isHost = room.localParticipant.identity.startsWith('host');
            const msgObj = new BeaconChatData(unsentMsg, user, sid, isHost);
            addChatMsg(msgObj);
            emitDatatrackMsg(msgObj);
            setUnsentMsg('');
            // Preserve the focus on the icon click
            Platform.OS === 'web' && (document === null || document === void 0 ? void 0 : document.getElementById('chatInput')).focus();
            debouncedScrollToEnd();
        }
    };
    /**
     * This handler toggles the chat visibility. Not super useful at the moment but this is
     * currently in place to support future functionality/features such as capturing current
     * scroll position on close, etc.
     */
    const onClose = () => {
        toggleChatVis();
    };
    /**
     * chatLog useEffect hook which iterates over the chat messages to generate the
     * appropriate DOM elements for display within the component.
     */
    const handleChatLogUpdate = () => {
        // TODO: properly type this
        const updatedMessages = chatLog.map(({ msg, user, sid, isHost }, index) => {
            const isFirstMsg = index === 0;
            const isSequential = isFirstMsg ? false : chatLog[index - 1].sid === chatLog[index].sid;
            const isCurrentUser = room.localParticipant.sid === sid;
            const messageEl = isCurrentUser
                // Current user msg style
                ? (React.createElement(DNABox, { style: styles.chatMsgBox, key: index, appearance: "col" },
                    React.createElement(DNAText, { style: mergeStyles(undefined, styles.chatMsg, styles.localChatMsg) }, msg)))
                // All other users msg style
                : (React.createElement(DNABox, { style: styles.chatMsgBox, key: index, appearance: "col" },
                    React.createElement(Iffy, { is: !isSequential },
                        React.createElement(DNAText, { style: isHost
                                ? styles.chatUserHost
                                : styles.chatUser },
                            user,
                            " ",
                            React.createElement(Iffy, { is: isHost }, "(Host)"))),
                    React.createElement(DNAText, { style: mergeStyles(undefined, styles.chatMsg, styles.remoteChatMsg) }, msg)));
            return messageEl;
        });
        setMessages(updatedMessages);
    };
    useEffect(handleChatLogUpdate, [chatLog]);
    /**
     * messages useEffect hook to autoscroll the chat body
     */
    const handleMessagesUpdate = () => {
        if (isScrolledToBottom) {
            debouncedScrollToEnd();
        }
    };
    useEffect(handleMessagesUpdate, [messages, isScrolledToBottom]);
    /**
     * chatVisible useEffect hook to autoscroll chat body to the end on chat component open
     */
    const handleChatVisible = () => {
        var _a;
        chatVisible && ((_a = chatBody.current) === null || _a === void 0 ? void 0 : _a.scrollToEnd({ animated: false }));
    };
    useEffect(handleChatVisible, [chatVisible]);
    const handleChatScroll = (scroll) => {
        const pos = scroll.nativeEvent;
        // Determine if we're at the bottom. Add some buffer in case user didn't scroll all the way down
        if (pos.contentOffset.y + pos.layoutMeasurement.height > pos.contentSize.height - 20) {
            setIsScrolledToBottom(true);
        }
        else {
            setIsScrolledToBottom(false);
        }
    };
    const handleChatLayout = () => {
        if (isScrolledToBottom) {
            debouncedScrollToEnd();
        }
    };
    return (React.createElement(Iffy, { is: isMobile ? chatVisible : true },
        React.createElement(DNABox, { style: isMobile ? styles.mobileContainer : styles.container, appearance: "col" },
            React.createElement(Iffy, { is: isMobile },
                React.createElement(DNABox, { fill: true, style: isMobile ? styles.mobileHeader : styles.header, alignY: "center" },
                    React.createElement(Iffy, { is: !isMobile },
                        React.createElement(DNAIcon, { style: mergeStyles(undefined, styles.headerElement, { marginRight: 19 }), name: "drag-vertical" })),
                    React.createElement(DNAIcon, { style: mergeStyles(undefined, styles.headerElement, { marginRight: 6 }), name: "chat-processing" }),
                    React.createElement(DNAText, { style: mergeStyles(undefined, styles.headerElement, isMobile
                            ? styles.mobileHeaderTitle
                            : styles.headerTitle) }, "Chat"),
                    React.createElement(Iffy, { is: !isMobile },
                        React.createElement(DNAButton, { status: "subtle", appearance: "ghostAlt", context: "minimum", onPress: onClose, iconLeft: 'chevron-down' })),
                    React.createElement(Iffy, { is: isMobile },
                        React.createElement(DNABox, { style: styles.mobileCloseHeader },
                            React.createElement(DNAButton, { appearance: "ghost", status: "primary", context: "minimum", size: "large", onPress: onClose }, "Close"))))),
            React.createElement(DNABox, { ref: chatBody, as: ScrollView, appearance: "col", onScroll: handleChatScroll, onLayout: handleChatLayout, scrollEventThrottle: 0, style: isMobile ? styles.mobileBody : styles.body }, messages),
            React.createElement(DNABox, { fill: true, appearance: "row", style: styles.inputWrapper, alignY: "center" },
                React.createElement(InputComponent, { value: unsentMsg, required: false, multiline: false, autoFocus: true, autoCompleteType: "off", title: "", onKeyPress: ({ nativeEvent }) => (nativeEvent.key === 'Enter' && onMsgSend()), titleColor: luxColors.contentText.primary, characterLimit: 500, placeHolder: "Type message here...", removeMarginPadding: true, returnKeyType: "send", onChangeText: onMsgEdit }),
                React.createElement(DNAButton, { status: "primary", appearance: "filled", context: "square", onPress: onMsgSend, iconLeft: "send", disabled: !unsentMsg, style: styles.sendButtonStyle })))));
};
//# sourceMappingURL=Chat.js.map